/**
 * Title, description and Open Graph data of one page in one language, per docs/specs/seo-meta-texts.md. Plain
 * data: the build script that writes the page manifest (run with vite-node) serialises it, and the client applies
 * it on navigation.
 *
 * No DOM, no React, and nothing that pulls the lib.spa-* packages, i18next or MDX bodies: the language-dependent
 * data comes in as PageHeadSources, so neither side bundles or loads the ten translation files (2+ MB each) just
 * for the head. The catalog is the bundled catalog.json, as in page-inventory.ts. Imports are relative (no `~/`
 * alias), as in src/entities/urls/localised-path.ts.
 */
import catalogJson from '../../data/catalog.json';
import type { LanguageCode } from '../../texts/languages';
import { uiTexts } from '../../texts/ui';
import { addProjectPrefix } from '../../utils/texts';
import { productSlug } from '../catalog/paths';
import type { SeoMetaTable } from '../catalog/seo-meta';
import { capitalizeFirst } from '../catalog/text';
import type { Catalog, Category, Product, SubcategoryPage } from '../catalog/types';
import type { PageInventoryEntry } from './page-inventory';

export type PageHead = {
  title: string;
  /** Omitted where seo-meta-texts.md has no text for the page in that language. */
  description?: string;
  ogType: 'website' | 'product' | 'article';
  /**
   * A product photo as a Content CDN path without a leading slash (`41732/24116_maxi.png`, resolved against the
   * environment's `contentCdnUrl`), or a site path with a leading slash (a blog cover from `public/images`). Made
   * absolute where the head tags are written. Products and blog articles only.
   */
  ogImagePath?: string;
};

/** The part of a blog article's `meta` export the head reads. `description` is the new optional field. */
export type BlogHeadMeta = { title: string; description?: string; coverImage?: string };

/**
 * The language-dependent data of one language, loaded once per language and reused for all of its pages.
 *
 * - translate: the text of a translation key (`WebProductCatalog:…`) in that language, undefined when the
 *   language has none. In the client, the active language's i18next resources; in the build script, the
 *   language's src/translations/<lang>.json (`translateFrom(table)`). Never fall back to another language.
 * - seoMeta: that language's table as `loadSeoMeta(lang)` resolves it (src/data/seo/<lang>.json, keyed by
 *   category or subcategory id; `{}` for a language without a file).
 * - blogMeta: the `meta` of the article in that language, e.g. `(slug) => getPostMeta(slug, lang)` in the client.
 */
export type PageHeadSources = {
  translate: (key: string) => string | undefined;
  seoMeta: SeoMetaTable;
  blogMeta: (slug: string) => BlogHeadMeta | undefined;
};

/** A translate function over a flat `{ key: text }` table such as src/translations/<lang>.json. */
export function translateFrom(table: Readonly<Record<string, string>>): PageHeadSources['translate'] {
  return (key) => table[key] || undefined;
}

const BRAND = 'JABLOTRON';
const TITLE_TARGET_LENGTH = 60;

/** Adds ` | JABLOTRON` unless the title already names the brand (seo-meta-texts.md, Společné zásady). */
function withBrand(title: string): string {
  return title.includes(BRAND) ? title : `${title} | ${BRAND}`;
}

/**
 * The cs texts of the "Ostatní stránky" table in docs/specs/seo-meta-texts.md, the only language they exist in.
 * The table's search title is `Hledání „{q}“ | JABLOTRON`; the inventory's /search entry has no query, so its title
 * drops the quoted part (`searchWithoutQuery`).
 */
const CS_STATIC_HEADS = {
  home: {
    title: 'Produktový katalog JABLOTRON',
    description:
      'Ústředny, detektory, klávesnice a příslušenství systémů JABLOTRON 100+ a Mercury. Technické parametry, návody a kompatibilita pro montážní partnery.',
  },
  products: {
    title: 'Všechny produkty | JABLOTRON',
    description:
      'Filtrujte ústředny, detektory, sirény a další prvky podle systému, typu připojení, umístění a barvy. Parametry a manuály u každého produktu.',
  },
  compatibility: {
    title: 'Kompatibilita prvků | JABLOTRON',
    description:
      'Zjistěte, které detektory, klávesnice a moduly fungují s ústřednou JABLOTRON 100+ nebo Mercury. Tabulka podle ústředny a typu připojení.',
  },
  blog: {
    title: 'Novinky a články | JABLOTRON',
    description: 'Případové studie, novinky z vývoje a firmware a odborné články pro montážní partnery JABLOTRON.',
  },
  search: { title: 'Hledání „{q}“ | JABLOTRON' },
  searchWithoutQuery: { title: 'Hledání | JABLOTRON' },
  notFound: { title: 'Stránka nenalezena | JABLOTRON' },
} as const;

/** Translation keys of the UI texts, as src/texts/runtime.ts prefixes them (PROJECT.WEB_PRODUCT_CATALOG). */
const uiKeys = addProjectPrefix('WebProductCatalog', uiTexts);

/**
 * For the other languages the title of a fixed page is the translated heading the page shows, without a trailing
 * full stop, plus the brand; there is no description (seo-meta-texts.md has none outside cs).
 */
const STATIC_TITLE_KEYS = {
  home: uiKeys.HeroTitle,
  products: uiKeys.CatalogAllProducts,
  compatibility: uiKeys.FooterProductCompatibility,
  blog: uiKeys.PostBreadcrumbBlog,
  search: uiKeys.SearchResultsFor,
  searchWithoutQuery: uiKeys.SearchResultsTitle,
  notFound: uiKeys.NotFoundHeading,
} as const;

type StaticPage = keyof typeof STATIC_TITLE_KEYS;

const STATIC_PATHS: Record<string, StaticPage> = {
  ['/']: 'home',
  ['/products']: 'products',
  ['/compatibility']: 'compatibility',
  ['/blog']: 'blog',
  ['/search']: 'searchWithoutQuery',
};

function staticHead(lang: LanguageCode, page: StaticPage, sources: PageHeadSources, query?: string): PageHead {
  if (lang === 'cs') {
    const { title, ...rest } = CS_STATIC_HEADS[page];

    return { title: query === undefined ? title : title.replace('{q}', query), ...rest, ogType: 'website' };
  }
  const heading = (sources.translate(STATIC_TITLE_KEYS[page]) ?? '').replace(/\.$/, '');
  const text = query === undefined ? heading : heading.replace('{{query}}', query);

  return { title: text ? withBrand(text) : BRAND, ogType: 'website' };
}

/** The head of the not-found page in a language. */
export function notFoundHead(lang: LanguageCode, sources: PageHeadSources): PageHead {
  return staticHead(lang, 'notFound', sources);
}

type CatalogIndex = {
  products: Map<string, Product>;
  categories: Map<string, Category>;
  subcategories: Map<string, { subcategory: SubcategoryPage; category: Category }>;
};

let catalogIndex: CatalogIndex | undefined;

/** The catalog pages by the segments of their internal paths, built on first use. */
function indexCatalog(): CatalogIndex {
  if (!catalogIndex) {
    const catalog = catalogJson as unknown as Catalog;
    const categorySlug = (category: Category) => category.slug || category.id;
    const categories = new Map(catalog.categories.map((category) => [categorySlug(category), category]));
    const categoriesById = new Map(catalog.categories.map((category) => [category.id, category]));
    const subcategories = new Map<string, { subcategory: SubcategoryPage; category: Category }>();
    for (const subcategory of catalog.subcategoryPages) {
      const category = categoriesById.get(subcategory.categoryId);
      if (category) {
        subcategories.set(`${categorySlug(category)}/${subcategory.slug || subcategory.id}`, { subcategory, category });
      }
    }
    catalogIndex = {
      products: new Map(catalog.products.map((product) => [productSlug(product.id), product])),
      categories,
      subcategories,
    };
  }

  return catalogIndex;
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * A subcategory name without the name of its line, which the title adds back from the category: "Ústředny Mercury",
 * "Mercury keypads" and "Mercury-Zentralen" under "JABLOTRON Mercury" become "Ústředny", "Keypads", "Zentralen".
 */
export function withoutLineName(subcategoryName: string, categoryName: string): string {
  const lineNames = [categoryName, categoryName.replace(new RegExp(`^${BRAND}\\s+`), '')].filter(Boolean);
  let name = subcategoryName;
  for (const lineName of lineNames) {
    name = name.replace(new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(lineName)}(?![\\p{L}\\p{N}])`, 'giu'), ' ');
  }
  // The hyphen of a compound ("Mercury-Zentralen") goes with the line name.
  name = name
    .replace(/(^|\s)-+|-+(?=\s|$)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

  return name ? capitalizeFirst(name) : subcategoryName;
}

/**
 * A CDN product photo in the largest rendition as PNG, which every link preview reads; a site path stays as is.
 * Also the product JSON-LD `image` (product-json-ld.ts), so both name the same picture.
 */
export function productOgImagePath(image: string): string | undefined {
  if (!image) {
    return undefined;
  }

  return image.startsWith('/') || /^https?:\/\//.test(image) ? image : `${image}_maxi.png`;
}

/** The product name in a language as the detail page's H1 shows it; empty when the language has none. */
export function productName(product: Product, translate: PageHeadSources['translate']): string {
  return capitalizeFirst(translate(product.name) ?? '');
}

function productHead(product: Product, { translate }: PageHeadSources): PageHead {
  const name = productName(product, translate);
  const title = name.toLowerCase().includes(product.code.toLowerCase()) ? name : `${name} ${product.code}`.trim();

  return { title: withBrand(title), ogType: 'product', ogImagePath: productOgImagePath(product.image) };
}

function categoryHead(category: Category, { translate, seoMeta }: PageHeadSources): PageHead {
  const seo = seoMeta[category.id];
  if (seo) {
    return { title: seo.title, description: seo.description, ogType: 'website' };
  }

  return { title: withBrand(translate(category.title) ?? BRAND), ogType: 'website' };
}

function subcategoryHead(
  subcategory: SubcategoryPage,
  category: Category,
  { translate, seoMeta }: PageHeadSources,
): PageHead {
  const seo = seoMeta[subcategory.id];
  if (seo) {
    return { title: seo.title, description: seo.description, ogType: 'website' };
  }
  const categoryName = translate(category.title) ?? '';
  const subcategoryName = translate(subcategory.title) ?? '';
  const title = `${withoutLineName(subcategoryName, categoryName)} ${categoryName}`.trim();

  return { title: withBrand(title || BRAND), ogType: 'website' };
}

function articleHead(meta: BlogHeadMeta): PageHead {
  const branded = withBrand(meta.title);

  return {
    title: branded.length <= TITLE_TARGET_LENGTH ? branded : meta.title,
    ...(meta.description ? { description: meta.description } : {}),
    ogType: 'article',
    ...(meta.coverImage ? { ogImagePath: meta.coverImage } : {}),
  };
}

/**
 * The head of a page of the inventory in a language. `searchQuery` is the `q` of a search page in the client; the
 * inventory's /search entry (and the server, which ignores queries) leaves it out.
 *
 * Throws for a path that is no page of the catalog or blog, or a blog article without `meta` in that language:
 * the inventory never lists one, so it means the inventory and the head data disagree.
 */
export function pageHead(
  lang: LanguageCode,
  { internalPath }: Pick<PageInventoryEntry, 'internalPath'>,
  sources: PageHeadSources,
  { searchQuery }: { searchQuery?: string } = {},
): PageHead {
  const staticPage = STATIC_PATHS[internalPath];
  if (staticPage) {
    return staticPage === 'searchWithoutQuery' && searchQuery
      ? staticHead(lang, 'search', sources, searchQuery)
      : staticHead(lang, staticPage, sources);
  }

  const [, section, first, second, ...rest] = internalPath.split('/');
  const catalog = indexCatalog();
  if (section === 'products' && first && !second) {
    const product = catalog.products.get(first);
    if (product) {
      return productHead(product, sources);
    }
  }
  if (section === 'categories' && first && !rest.length) {
    if (!second) {
      const category = catalog.categories.get(first);
      if (category) {
        return categoryHead(category, sources);
      }
    } else {
      const found = catalog.subcategories.get(`${first}/${second}`);
      if (found) {
        return subcategoryHead(found.subcategory, found.category, sources);
      }
    }
  }
  if (section === 'blog' && first && !second) {
    const meta = sources.blogMeta(first);
    if (meta) {
      return articleHead(meta);
    }
  }

  throw new Error(`pageHead: no ${lang} page at ${internalPath}`);
}
